import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronDown, ChevronRight, Loader2, Lock, Plus, Save, Target, Trash2 } from 'lucide-react';
import useApi from '../../api';
import AgentTabHeader from './AgentTabHeader';
import MentionEditor from '../builder/MentionEditor';
import { buildMentionables, Mentionable, MentionableUser } from '../builder/promptMentions';
import { AgentSkill } from './types';
import { StageBadge, useLifecycleStages } from '../../accounts/lifecycleStages';

/**
 * Objectives tab (agent builder, shared between website and app modes).
 * Objectives are the customer-journey goals this agent works toward and
 * tracks. Like the rest of the builder they are draft/publish gated: edits
 * land on the draft set and reach live sessions when the agent is published
 * (no separate feature toggle — an agent with active objectives has the
 * feature on).
 */

export interface ObjectiveDefinition {
  id?: string;
  key: string;
  name: string;
  description: string;
  level: 'account' | 'person';
  stage: string | null;
  gating: 'advisory' | 'locked';
  completion_sources: string[] | null;
  sort_order: number;
  active: boolean;
  /**
   * Shown to the customer's own users as a checklist item through the
   * Services SDK (GET /sdk/v1/objectives) — name only, the description
   * stays private. Off by default: most objectives are internal rubric.
   */
  customer_facing: boolean;
  /** Relative to the live set: 'new' | 'modified' | 'published'. */
  publish_state?: string;
  /**
   * Recorded progress on the live twin (instances anchor only to the live
   * set). Null/absent when nothing has been recorded. Once progress exists
   * the objective can only be deactivated, never deleted — the history lives
   * on contacts and accounts.
   */
  progress?: { accounts: number; people: number } | null;
}

const hasProgress = (definition: ObjectiveDefinition) =>
  !!definition.progress && definition.progress.accounts + definition.progress.people > 0;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const progressLabel = (definition: ObjectiveDefinition) => {
  if (!hasProgress(definition)) return null;
  const { accounts, people } = definition.progress!;
  return [accounts > 0 && plural(accounts, 'account'), people > 0 && plural(people, 'contact')]
    .filter(Boolean)
    .join(' · ');
};

const SOURCES = ['ai', 'sdk', 'manual', 'crm'] as const;

const SOURCE_LABELS: Record<string, string> = {
  ai: 'AI agent',
  sdk: 'Your SDK',
  manual: 'Dashboard',
  crm: 'CRM',
};

const slugifyKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

const emptyDefinition = (sortOrder: number): ObjectiveDefinition => ({
  key: '',
  name: '',
  description: '',
  level: 'account',
  stage: null,
  gating: 'advisory',
  completion_sources: null,
  sort_order: sortOrder,
  active: true,
  customer_facing: false,
});

const DefinitionEditor = ({
  value,
  isNew,
  stages,
  saving,
  mentionables,
  onChange,
  onSave,
  onCancel,
}: {
  value: ObjectiveDefinition;
  isNew: boolean;
  stages: { key: string; label: string }[];
  saving: boolean;
  mentionables: Mentionable[];
  onChange: (next: ObjectiveDefinition) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const restrictedCompletion = value.completion_sources !== null;
  return (
    <div className="space-y-4 border-t px-4 py-4">
      <div className="grid gap-4 @lg:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Name</Label>
          <Input
            value={value.name}
            maxLength={80}
            placeholder="e.g. Trial started"
            onChange={(e) => {
              const name = e.target.value;
              onChange({
                ...value,
                name,
                // Key follows the name until first save; after that it is the
                // SDK contract and stays put.
                key: isNew ? slugifyKey(name) : value.key,
              });
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Key {isNew ? '(from name)' : '(immutable)'}</Label>
          <Input value={value.key} disabled className="font-mono text-xs" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">
          Description — the AI's rubric for what "done" means. Type @ to reference form fields,
          visitor data, tools, or team members.
        </Label>
        <MentionEditor
          value={value.description}
          onChange={(description) => onChange({ ...value, description })}
          mentionables={mentionables}
          placeholder="e.g. The account has an active trial. Done when the visitor confirms they started a trial, or your product reports it via the SDK."
        />
      </div>
      <div className="grid gap-4 @lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Level</Label>
          <Select
            value={value.level}
            onValueChange={(level) => onChange({ ...value, level: level as 'account' | 'person' })}
            disabled={!isNew}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="account">Account — once per company</SelectItem>
              <SelectItem value="person">Person — per contact</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Lifecycle stage</Label>
          <Select
            value={value.stage || 'none'}
            onValueChange={(stage) =>
              onChange({
                ...value,
                stage: stage === 'none' ? null : stage,
                gating: stage === 'none' ? 'advisory' : value.gating,
              })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any stage</SelectItem>
              {stages.map((stage) => (
                <SelectItem key={stage.key} value={stage.key}>
                  {stage.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Stage gating</Label>
          <Select
            value={value.gating}
            onValueChange={(gating) => onChange({ ...value, gating: gating as 'advisory' | 'locked' })}
            disabled={!value.stage}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="advisory">Advisory — guides the AI's focus</SelectItem>
              <SelectItem value="locked">Locked — no progress before this stage</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`restrict-${value.key || 'new'}`}
            checked={restrictedCompletion}
            onCheckedChange={(checked) =>
              onChange({ ...value, completion_sources: checked ? ['sdk', 'manual'] : null })
            }
          />
          <Label htmlFor={`restrict-${value.key || 'new'}`} className="text-xs font-normal">
            Restrict who can mark this done (e.g. externally verified milestones the AI should
            never complete on its own)
          </Label>
        </div>
        {restrictedCompletion && (
          <div className="flex flex-wrap items-center gap-4 pl-6">
            {SOURCES.map((source) => (
              <div key={source} className="flex items-center gap-1.5">
                <Checkbox
                  id={`source-${source}-${value.key || 'new'}`}
                  checked={(value.completion_sources || []).includes(source)}
                  onCheckedChange={(checked) => {
                    const current = new Set(value.completion_sources || []);
                    if (checked) current.add(source);
                    else current.delete(source);
                    onChange({ ...value, completion_sources: Array.from(current) });
                  }}
                />
                <Label htmlFor={`source-${source}-${value.key || 'new'}`} className="text-xs font-normal">
                  {SOURCE_LABELS[source]}
                </Label>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={`customer-facing-${value.key || 'new'}`}
          checked={value.customer_facing}
          onCheckedChange={(checked) => onChange({ ...value, customer_facing: checked === true })}
        />
        <Label htmlFor={`customer-facing-${value.key || 'new'}`} className="text-xs font-normal">
          Show to your users — appears in the checklist your product renders through the SDK
          (name only; the description stays private)
        </Label>
      </div>
      <div className="flex items-center gap-3 pt-1">
        <Button type="button" size="sm" className="gap-1.5" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {isNew ? 'Create objective' : 'Save changes'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default function ObjectivesTab({
  agId,
  form,
  skills,
}: {
  agId: string;
  form?: any[];
  skills?: AgentSkill[];
}) {
  const { get, post, put, remove } = useApi();
  const { stages } = useLifecycleStages();
  const [definitions, setDefinitions] = useState<ObjectiveDefinition[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<MentionableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ObjectiveDefinition | null>(null);
  const [newDraft, setNewDraft] = useState<ObjectiveDefinition | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!agId) return;
    get(`/agents/${agId}/objectives`)
      .then((response) => setDefinitions(response.data?.items || []))
      .catch((err) => {
        console.error('Error fetching objectives:', err);
        setError('Failed to load objectives');
      })
      .finally(() => setLoading(false));
    // Org users back the Team group in the description @-mention menu.
    get('/users/')
      .then((response) => setOrganizationUsers(response.data || []))
      .catch((err) => console.error('Error fetching organization users:', err));
  }, [agId]);

  // Local definitions (not a refetch) back the Objectives mention group, so
  // the catalog reflects unsaved edits and newly added objectives.
  const mentionables = useMemo(
    () =>
      buildMentionables(form || [], skills || [], {
        users: organizationUsers,
        objectives: definitions.filter((d) => d.key && d.active !== false),
      }),
    [form, skills, organizationUsers, definitions]
  );

  const sorted = useMemo(
    () => [...definitions].sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key)),
    [definitions]
  );
  const activeDefinitions = useMemo(() => sorted.filter((d) => d.active), [sorted]);
  const inactiveDefinitions = useMemo(() => sorted.filter((d) => !d.active), [sorted]);
  // Inactive objectives are out of the AI's prompt; they stay reachable for
  // reactivation but collapsed so the working list is the active set.
  const [showInactive, setShowInactive] = useState(false);

  const saveDefinition = async (definition: ObjectiveDefinition, isNew: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const response = isNew
        ? await post(`/agents/${agId}/objectives`, definition)
        : await put(`/agents/${agId}/objectives/${definition.id}`, definition);
      const saved = response.data as ObjectiveDefinition;
      setDefinitions((current) =>
        isNew ? [...current, saved] : current.map((d) => (d.id === saved.id ? { ...d, ...saved } : d))
      );
      setNewDraft(null);
      setDraft(null);
      setExpandedId(null);
      toast.success(isNew ? 'Objective created' : 'Objective saved');
      for (const warning of (response.data as any).mention_warnings || []) {
        toast.warn(`${warning}. The AI will see only the raw identifier.`);
      }
    } catch (err: any) {
      setError(err?.data?.detail || 'Failed to save objective');
    } finally {
      setSaving(false);
    }
  };

  const deleteDefinition = async (definition: ObjectiveDefinition) => {
    try {
      await remove(`/agents/${agId}/objectives/${definition.id}`);
      setDefinitions((current) => current.filter((d) => d.id !== definition.id));
      toast.success('Objective deleted');
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Failed to delete objective');
    }
  };

  const toggleActive = async (definition: ObjectiveDefinition, active: boolean) => {
    try {
      const response = await put(`/agents/${agId}/objectives/${definition.id}`, {
        ...definition,
        active,
      });
      const saved = response.data as ObjectiveDefinition;
      setDefinitions((current) => current.map((d) => (d.id === saved.id ? { ...d, ...saved } : d)));
      // The row just moved sections; keep it visible so switching off
      // doesn't read as a deletion.
      if (!active) setShowInactive(true);
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Failed to update objective');
    }
  };

  const renderRow = (definition: ObjectiveDefinition) => {
    const isExpanded = expandedId === definition.id;
    return (
      <div key={definition.id} className="rounded-lg border bg-card">
        <div className="flex items-center gap-3 px-4 py-2.5">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-start gap-3 text-left"
            onClick={() => {
              if (isExpanded) {
                setExpandedId(null);
                setDraft(null);
              } else {
                setExpandedId(definition.id || null);
                setDraft({ ...definition });
                setNewDraft(null);
              }
            }}
          >
            {isExpanded ? (
              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            {/* Name first, then the meta badges. They wrap onto their own line
                when the column is narrow instead of squeezing the name to zero. */}
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
              <span className={`min-w-0 max-w-full truncate text-sm font-medium ${definition.active ? '' : 'text-muted-foreground line-through'}`}>
                {definition.name}
              </span>
              <code className="hidden text-[11px] text-muted-foreground @lg:inline">
                {definition.key}
              </code>
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="shrink-0 text-[10px] uppercase">
                  {definition.level}
                </Badge>
                {definition.stage && (
                  <span className="inline-flex shrink-0">
                    <StageBadge stageKey={definition.stage} stages={stages} />
                  </span>
                )}
                {definition.gating === 'locked' && (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">
                    Locked
                  </Badge>
                )}
                {definition.customer_facing && (
                  <Badge variant="outline" className="shrink-0 text-[10px]" title="Visible to your users through the SDK">
                    Customer-facing
                  </Badge>
                )}
                {definition.publish_state === 'new' && (
                  <Badge className="shrink-0 bg-blue-100 text-[10px] text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300">
                    Draft
                  </Badge>
                )}
                {definition.publish_state === 'modified' && (
                  <Badge className="shrink-0 bg-amber-100 text-[10px] text-amber-700 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300">
                    Edited
                  </Badge>
                )}
                {hasProgress(definition) && (
                  <span
                    className="inline text-[11px] text-muted-foreground @md:hidden"
                    title="Recorded progress on the live agent"
                  >
                    {progressLabel(definition)}
                  </span>
                )}
              </span>
            </span>
          </button>
          {hasProgress(definition) && (
            <span
              className="hidden shrink-0 text-[11px] text-muted-foreground @md:inline"
              title="Recorded progress on the live agent"
            >
              {progressLabel(definition)}
            </span>
          )}
          <Switch
            checked={definition.active}
            onCheckedChange={(active) => toggleActive(definition, active)}
            title={definition.active ? 'Active' : 'Inactive'}
          />
          {hasProgress(definition) ? (
            // Delete is refused server-side once progress exists; don't
            // offer an affordance that can only fail. Keep the slot so
            // rows align.
            <span
              className="inline-flex h-7 w-7 items-center justify-center text-muted-foreground/40"
              title="Can't delete — this objective has recorded progress. Deactivate it instead; history is preserved."
            >
              <Lock className="h-3.5 w-3.5" />
            </span>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => deleteDefinition(definition)}
              title="Delete objective"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {isExpanded && draft && (
          <DefinitionEditor
            value={draft}
            isNew={false}
            stages={stages}
            saving={saving}
            mentionables={mentionables}
            onChange={setDraft}
            onSave={() => saveDefinition(draft, false)}
            onCancel={() => {
              setExpandedId(null);
              setDraft(null);
            }}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[350px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <AgentTabHeader
        icon={Target}
        title="Objectives"
        description="Define the journey goals this agent works toward and tracks."
        actions={
          <Button
            type="button"
            size="sm"
            className="gap-2"
            onClick={() => {
              setNewDraft(emptyDefinition((definitions.length + 1) * 10));
              setExpandedId(null);
              setDraft(null);
            }}
            disabled={newDraft !== null}
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden @md:inline">Add objective</span>
            <span className="@md:hidden">Add</span>
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-4xl space-y-6 py-6">
        <section className="space-y-3">
          <p className="text-xs leading-5 text-muted-foreground">
            Objectives span the customer journey — from qualification on your website to setup
            milestones inside your product, across both website and app modes. The AI tracks
            progress conversation by conversation, and your product can sync statuses through
            the SDK when users do things themselves. Account-level objectives complete once per
            company; person-level objectives are tracked per contact. Changes are saved to the
            draft and take effect when you publish the agent.
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {newDraft && (
            <div className="rounded-lg border bg-card">
              <div className="px-4 py-3 text-sm font-semibold">New objective</div>
              <DefinitionEditor
                value={newDraft}
                isNew
                stages={stages}
                saving={saving}
                mentionables={mentionables}
                onChange={setNewDraft}
                onSave={() => saveDefinition(newDraft, true)}
                onCancel={() => setNewDraft(null)}
              />
            </div>
          )}

          <div className="space-y-2">
            {activeDefinitions.map(renderRow)}
            {activeDefinitions.length === 0 && inactiveDefinitions.length > 0 && !newDraft && (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No active objectives. The AI isn't working toward anything — reactivate one below
                or add a new objective.
              </div>
            )}
            {!sorted.length && !newDraft && (
              <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                No objectives yet. Add your first — for example "Understand who they are",
                "Demo booked", or "Completed onboarding".
              </div>
            )}
          </div>

          {inactiveDefinitions.length > 0 && (
            <div className="space-y-2 pt-2">
              <button
                type="button"
                className="flex w-full items-center gap-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                onClick={() => setShowInactive((current) => !current)}
              >
                {showInactive ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                Inactive ({inactiveDefinitions.length})
              </button>
              {showInactive && (
                <>
                  <p className="text-xs leading-5 text-muted-foreground">
                    Inactive objectives are hidden from the AI and stop accepting status updates.
                    Ones with recorded progress can't be deleted — their history stays on the
                    contacts and accounts that reached them. Flip the switch to bring one back.
                  </p>
                  <div className="space-y-2">{inactiveDefinitions.map(renderRow)}</div>
                </>
              )}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
